import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
} from "react";
import type { TeamPerson, PersonalStaffInvitationCreate, StaffInvitationCandidate } from "@/libs/staffApi";
import { AuthContext } from "@bw/shared/hooks";

type Slot<T> = { current: T | null };
type SuspendIntent = { memberId: string; key: string };

type PeopleCommandMemory = {
  createKey: Slot<string>;
  createIntent: Slot<PersonalStaffInvitationCreate>;
  suspendIntent: Slot<SuspendIntent>;
};

const emptyForm = {
  display_name: "",
  email: "",
  staff_management_enabled: false,
};
type Setter<T> = Dispatch<SetStateAction<T>>;
type PeopleWorkspaceMemory = PeopleCommandMemory & {
  busy: { current: boolean };
  working: boolean;
  setWorking: Setter<boolean>;
  createOpen: boolean;
  setCreateOpen: Setter<boolean>;
  invitationTarget: TeamPerson | null;
  setInvitationTarget: Setter<TeamPerson | null>;
  form: typeof emptyForm;
  setForm: Setter<typeof emptyForm>;
  selectedUser: StaffInvitationCandidate | null;
  setSelectedUser: Setter<StaffInvitationCandidate | null>;
  inviteByEmail: boolean;
  setInviteByEmail: Setter<boolean>;
  facilityRoles: Record<string, string[]>;
  setFacilityRoles: Setter<Record<string, string[]>>;
  suspendTarget: TeamPerson | null;
  setSuspendTarget: Setter<TeamPerson | null>;
  notice: string | null;
  setNotice: Setter<string | null>;
  error: string | null;
  setError: Setter<string | null>;
};
const Context = createContext<PeopleWorkspaceMemory | null>(null);

function clear(memory: PeopleCommandMemory) {
  for (const slot of Object.values(memory)) slot.current = null;
}

/** Page-scoped command lifetime, never Query/cache/storage persistence.
 *
 * Staff controls may unmount while authority is revalidated. A submitted,
 * uncertain command must survive that pause with its original body and UUID.
 * Explicit cancellation, definitive denial, scope/route change dispose it.
 */
type BoundaryProps = {
  children: ReactNode;
  allowed?: boolean;
  pending?: boolean;
};

export function PeopleCommandBoundary(props: BoundaryProps) {
  const auth = useContext(AuthContext);
  // Identity selects lifetime only, never authority. An accepted account switch
  // disposes the old instance so its late callbacks cannot mutate the new one.
  return <PeopleCommandScope key={JSON.stringify(auth?.user?.uid ?? null)} {...props} />;
}

function PeopleCommandScope({
  children,
  allowed = true,
  pending = false,
}: BoundaryProps) {
  const memory = useMemo<PeopleCommandMemory>(
    () => ({
      createKey: { current: null },
      createIntent: { current: null },
      suspendIntent: { current: null },
    }),
    [],
  );
  const busy = useRef(false);
  const [working, setWorking] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [invitationTarget, setInvitationTarget] = useState<TeamPerson | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedUser, setSelectedUser] = useState<StaffInvitationCandidate | null>(null);
  const [inviteByEmail, setInviteByEmail] = useState(false);
  const [facilityRoles, setFacilityRoles] = useState<Record<string, string[]>>(
    {},
  );
  const [suspendTarget, setSuspendTarget] = useState<TeamPerson | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => () => clear(memory), [memory]);
  useEffect(() => {
    if (!allowed && !pending) {
      clear(memory);
      busy.current = false;
      setWorking(false);
      setCreateOpen(false);
      setInvitationTarget(null);
      setForm(emptyForm);
      setSelectedUser(null);
      setInviteByEmail(false);
      setFacilityRoles({});
      setSuspendTarget(null);
      setNotice(null);
      setError(null);
    }
  }, [allowed, pending, memory]);
  return (
    <Context.Provider
      value={{
        ...memory,
        busy,
        working,
        setWorking,
        createOpen,
        setCreateOpen,
        invitationTarget,
        setInvitationTarget,
        form,
        setForm,
        selectedUser,
        setSelectedUser,
        inviteByEmail,
        setInviteByEmail,
        facilityRoles,
        setFacilityRoles,
        suspendTarget,
        setSuspendTarget,
        notice,
        setNotice,
        error,
        setError,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function usePeopleCommandMemory() {
  return useContext(Context);
}

export function useRequiredPeopleCommandMemory() {
  const memory = usePeopleCommandMemory();
  if (!memory) throw new Error("People commands require their scope boundary");
  return memory;
}
